import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

/**
 * A form whose values are loaded from the server and edited here.
 *
 * THE RULE (D205): A FAILED SAVE NEVER DESTROYS WHAT WAS TYPED. While a person
 * is editing, the values on screen are authoritative:
 *
 *  - The form takes the server's copy when it first arrives, and takes it
 *    again whenever it changes WHILE NOTHING HAS BEEN TYPED (another panel on
 *    the same page saved, a refetch brought a colleague's change).
 *  - Once anything has been typed, no refetch, invalidation or other save
 *    replaces it. A failed save changes nothing at all: the values, the page
 *    and the scroll position stay where they were.
 *  - Only a SUCCESSFUL save resets the form, from the copy the server returned
 *    — and not even then if the person kept typing while the save was in
 *    flight: those keystrokes are newer than what was saved.
 *
 * It replaces `useEffect(() => setForm(from(data)), [data])`, which overwrote
 * a half-edited form every time the query refetched (found on the Settings
 * page, where saving one panel reset every other).
 */
export function useServerForm<Data, Values extends object>(
  data: Data | undefined,
  toValues: (data: Data) => Values,
  empty: Values,
) {
  const [values, setValues] = useState<Values>(() =>
    data === undefined ? empty : toValues(data),
  );
  const adopted = useRef<Data | undefined>(data);
  /** Every local edit counts; the form is clean while this equals `cleanAt`. */
  const edits = useRef(0);
  const cleanAt = useRef(0);
  const convert = useRef(toValues);
  convert.current = toValues;

  /**
   * Take a server copy — but keep the current object when the values are the
   * same, so a copy that changed nothing on screen causes no render (and a
   * caller that builds its data afresh on every render cannot loop).
   */
  const adopt = useCallback((next: Values) => {
    setValues((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
  }, []);

  useEffect(() => {
    if (data === undefined || data === adopted.current) return;
    adopted.current = data;
    if (edits.current === cleanAt.current) adopt(convert.current(data));
  }, [data, adopt]);

  const set = useCallback(<Key extends keyof Values>(key: Key, value: Values[Key]) => {
    edits.current += 1;
    setValues((current) => ({ ...current, [key]: value }));
  }, []);

  /** Taken as a save starts; handed back to `saved` once it succeeds. */
  const mark = useCallback(() => edits.current, []);

  /** After a SUCCESSFUL save only, with the copy the server returned. */
  const saved = useCallback((copy: Data, markAtSave: number) => {
    adopted.current = copy;
    if (edits.current !== markAtSave) return;
    cleanAt.current = edits.current;
    adopt(convert.current(copy));
  }, [adopt]);

  return {
    values,
    set,
    mark,
    saved,
    isDirty: edits.current !== cleanAt.current,
  };
}

/**
 * Fill an edit form from its loaded record ONCE (D205): when the record first
 * arrives, or when the page moves to a different record — never again because
 * the same record was refetched. The effect this replaces,
 * `useEffect(() => fill(query.data), [query.data])`, ran on every refetch that
 * brought a changed copy and overwrote whatever the person had typed. Every
 * edit page that uses this leaves the form after a successful save, so there
 * is nothing to re-fill.
 */
export function useLoadOnce<Data extends { readonly id: string }>(
  data: Data | undefined,
  fill: (record: Data) => void,
) {
  const loaded = useRef<string | null>(null);
  const run = useRef(fill);
  run.current = fill;
  useEffect(() => {
    if (!data || loaded.current === data.id) return;
    loaded.current = data.id;
    run.current(data);
  }, [data]);
}

const FOCUSABLE =
  'input:not([type="hidden"]), textarea, select, button, [tabindex]:not([tabindex="-1"])';

/**
 * Bring the first field marked invalid inside `root` into view and focus it,
 * without touching its value. The first in DOM order is the first a person
 * reads. False when no field is marked — the caller then points at its
 * general message instead.
 */
export function focusFirstInvalid(root: HTMLElement | null): boolean {
  if (!root) return false;
  const marked = root.querySelector<HTMLElement>('[aria-invalid="true"], [data-invalid="true"]');
  if (!marked) return false;
  const target = marked.matches(FOCUSABLE)
    ? marked
    : (marked.querySelector<HTMLElement>(FOCUSABLE) ?? marked);
  // Centred, so the label above the field is in view as well.
  target.scrollIntoView?.({ block: 'center' });
  target.focus({ preventScroll: true });
  return true;
}

/**
 * When a save fails: the first field the server named receives focus; a
 * failure that names no field (network, timeout, 409, 500) moves focus to the
 * general message, so a keyboard or screen-reader user learns of it either way.
 */
export function useSaveFailureFocus(
  error: unknown,
  root: RefObject<HTMLElement | null>,
  summary: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!error) return;
    if (focusFirstInvalid(root.current)) return;
    summary.current?.focus();
    summary.current?.scrollIntoView?.({ block: 'nearest' });
  }, [error, root, summary]);
}
